import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  Bolt as BoltIcon,
  Whatshot as HotSeatIcon,
  EmojiEvents as TrophyIcon,
  CallSplit as SplitIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as SparkleIcon,
} from "@mui/icons-material";
import { BEER, LobbyStage, BrandLockup, BeerMug, StatusPill, Dots } from "./BeerillionerLobbyStage";

const HOW_IT_WORKS = [
  {
    icon: BoltIcon,
    color: BEER.blue,
    title: "Fastest Finger First",
    body: "Beat the room to the right answer.",
  },
  {
    icon: HotSeatIcon,
    color: BEER.amber,
    title: "Take the Hot Seat",
    body: "Quickest finger plays for the room.",
  },
  {
    icon: TrophyIcon,
    color: BEER.gold,
    title: "Climb the Ladder",
    body: "Every rung pours a bigger prize.",
  },
];

const TIPS = [
  "Lock in fast — ties are broken by milliseconds.",
  "Checkpoints keep your winnings safe. Aim for them.",
  "Not in the Hot Seat? You still vote with the audience.",
  "Turn your sound on — the music is half the game.",
  "Three lifelines. No refills. Spend them wisely.",
];

const lifelineIcon = (type) =>
  type === "50_50" ? SplitIcon : type === "ask_audience" ? GroupsIcon : PhoneIcon;

const BeerillionerWaitingView = ({
  config,
  displayName,
  spectatorMode = false,
  apiBase = "",
}) => {
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 5000);
    return () => clearInterval(id);
  }, []);

  const prizes = config?.prizes || [];
  const topPrize = useMemo(() => {
    if (!prizes.length) return null;
    return [...prizes].sort((a, b) => (b.level || 0) - (a.level || 0))[0];
  }, [prizes]);
  const lifelines = (config?.lifelines || []).filter((l) => l.is_enabled);
  const prizeImg = topPrize?.image_url
    ? topPrize.image_url.startsWith("http")
      ? topPrize.image_url
      : `${apiBase}/${topPrize.image_url}`
    : null;

  return (
    <LobbyStage>
      <BeerMug size={132} delay={0.05} />

      <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
        <BrandLockup delay={0.15} />
      </Box>

      <Typography
        sx={{
          color: "#fff",
          opacity: 0.72,
          fontSize: { xs: "0.85rem", sm: "1rem" },
          fontWeight: 500,
          letterSpacing: 0.4,
          mt: { xs: 1.2, sm: 1.6 },
          animation: "beerFadeUp 0.8s ease 0.35s both",
        }}
      >
        The pub quiz where every right answer pours a prize.
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          maxWidth: 420,
          my: { xs: 2.4, sm: 3 },
          animation: "beerFadeIn 0.8s ease 0.5s both",
        }}
      >
        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${BEER.gold}77)` }} />
        <SparkleIcon sx={{ color: BEER.gold, fontSize: 16, opacity: 0.8 }} />
        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${BEER.gold}77, transparent)` }} />
      </Box>

      {/* Live status — the thing players are actually waiting on */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          maxWidth: 480,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.8, sm: 2.2 },
          borderRadius: "26px",
          bgcolor: BEER.panelSoft,
          border: `2px solid ${BEER.gold}66`,
          boxShadow: "0 0 30px rgba(255,215,0,0.14), inset 0 0 40px rgba(41,182,246,0.06)",
          animation: "beerFadeUp 0.8s ease 0.6s both",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: "35%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)",
            animation: "beerSweep 4.5s ease-in-out infinite",
          },
        }}
      >
        <StatusPill blink color={BEER.gold} delay={0.7} sx={{ mb: 1 }}>
          WAITING FOR THE HOST
        </StatusPill>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.2rem" },
            lineHeight: 1.35,
          }}
        >
          The taps aren&apos;t open yet.
        </Typography>
        <Typography
          sx={{
            color: BEER.glow,
            fontSize: { xs: "0.85rem", sm: "0.95rem" },
            mt: 0.5,
          }}
        >
          Hang tight — the game starts the moment your host says go
          <Dots />
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 1,
          mt: 1.8,
          animation: "beerFadeUp 0.8s ease 0.75s both",
        }}
      >
        {spectatorMode ? (
          <StatusPill color={BEER.blue} delay={0.8}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
              <VisibilityIcon sx={{ fontSize: 15 }} /> Spectator view
            </Box>
          </StatusPill>
        ) : (
          <StatusPill color={BEER.green} delay={0.8}>
            You&apos;re in as {displayName || "Guest"}
          </StatusPill>
        )}
        {topPrize && (
          <StatusPill color={BEER.gold} delay={0.86}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
              {prizeImg ? (
                <Box
                  component="img"
                  src={prizeImg}
                  alt=""
                  sx={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <TrophyIcon sx={{ fontSize: 15 }} />
              )}
              Top prize: {topPrize.label || topPrize.value_text || `Level ${topPrize.level}`}
            </Box>
          </StatusPill>
        )}
        {prizes.length > 0 && (
          <StatusPill color={BEER.blue} delay={0.92}>
            {prizes.length} rungs
          </StatusPill>
        )}
      </Box>

      {/* How the night runs */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: { xs: 1, sm: 1.5 },
          width: "100%",
          maxWidth: 720,
          mt: { xs: 2.6, sm: 3.4 },
        }}
      >
        {HOW_IT_WORKS.map((step, i) => {
          const Icon = step.icon;
          return (
            <Box
              key={step.title}
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "column" },
                alignItems: "center",
                textAlign: { xs: "left", sm: "center" },
                gap: { xs: 1.4, sm: 0.8 },
                px: { xs: 1.8, sm: 1.5 },
                py: { xs: 1.3, sm: 2 },
                borderRadius: "20px",
                bgcolor: "rgba(13,33,55,0.55)",
                border: `1px solid ${BEER.border}`,
                animation: `beerFadeUp 0.7s ease ${1 + i * 0.12}s both`,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: `${step.color}1f`,
                  border: `1px solid ${step.color}55`,
                }}
              >
                <Icon sx={{ color: step.color, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: { xs: "0.88rem", sm: "0.9rem" },
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{ color: "#8fb3cc", fontSize: { xs: "0.76rem", sm: "0.78rem" }, mt: 0.2 }}
                >
                  {step.body}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {lifelines.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 0.8,
            mt: { xs: 2.2, sm: 2.6 },
            animation: "beerFadeUp 0.7s ease 1.4s both",
          }}
        >
          <Typography
            sx={{
              color: BEER.blue,
              fontWeight: 800,
              fontSize: "0.68rem",
              letterSpacing: 2,
              mr: 0.5,
            }}
          >
            LIFELINES
          </Typography>
          {lifelines.map((ll) => {
            const Icon = lifelineIcon(ll.type);
            return (
              <Box
                key={ll.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.4,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "rgba(255,215,0,0.08)",
                  border: "1px solid rgba(255,215,0,0.3)",
                }}
              >
                <Icon sx={{ color: BEER.gold, fontSize: 15 }} />
                <Typography sx={{ color: BEER.gold, fontSize: "0.72rem", fontWeight: 700 }}>
                  {ll.label || ll.type}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          mt: { xs: 2.6, sm: 3.2 },
          minHeight: 22,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          animation: "beerFadeIn 0.8s ease 1.6s both",
        }}
      >
        <SparkleIcon sx={{ color: BEER.amber, fontSize: 15, opacity: 0.9 }} />
        <Typography
          key={tipIndex}
          sx={{
            color: "#7f9db3",
            fontSize: { xs: "0.75rem", sm: "0.82rem" },
            fontStyle: "italic",
            animation: "beerFadeIn 0.6s ease both",
          }}
        >
          {TIPS[tipIndex]}
        </Typography>
      </Box>
    </LobbyStage>
  );
};

export default BeerillionerWaitingView;
